import { default as classNames, default as cx } from 'classnames'
import { BannerHalfBlurred } from 'components/common/BannerHalfBlurred'
import { BannerContent } from 'components/how-it-works/BannerContent'
import { ArtistsIds } from 'constants/artists'
import { useDisableScroll } from 'hooks/useDisableScroll'
import { useIsMounted } from 'hooks/useIsMounted'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import {
  ComponentProps,
  PropsWithChildren,
  Suspense,
  useCallback,
  useRef,
  useState,
} from 'react'
import { getArtistUrl } from 'utils/urls'
import classes from '../../artworks.module.sass'
const Filters = dynamic(
  () => import('components/artworks/Filters').then(m => m.Filters),
  { ssr: false }
)
const InstantSearch = dynamic(
  () => import('components/common/InstantSearch').then(m => m.InstantSearch),
  { ssr: false }
)
const Mobile = dynamic(
  () => import('components/artworks/Mobile').then(m => m.Mobile),
  { ssr: false }
)
const Header = dynamic(
  () => import('components/artworks/Header').then(m => m.Header),
  { ssr: false }
)
const List = dynamic(
  () => import('components/artworks/List').then(m => m.List),
  { ssr: false }
)
const Configure = dynamic(
  () => import('react-instantsearch').then(m => m.Configure),
  { ssr: false }
)

type Props = {
  instantSearch?: Omit<
    ComponentProps<typeof InstantSearch>,
    'index' | 'children'
  >
  hideBanner?: boolean
} & PropsWithChildren

const artists = [
  { name: 'Ai Weiwei', id: ArtistsIds.aiWeiWei },
  { name: 'Banksy', id: ArtistsIds.banksy },
  { name: 'Damien Hirst', id: ArtistsIds.damienHirst },
  { name: 'David Hockney', id: ArtistsIds.davidHockney },
  { name: 'Kaws', id: ArtistsIds.kaws },
  { name: 'Tracey Emin', id: ArtistsIds.traceyEmin },
  { name: 'David Shrigley', id: ArtistsIds.davidShrigley },
  { name: 'Werner Bronkhorst', id: ArtistsIds.wernerBronkhorst },
]

// From 1000s of artworks by leading contemporary artists including: Ai Weiwei, Banksy, Damien Hirst, David Hockney, Kaws, Tracey Emin, David Shrigley, Werner Bronkhorst.

export const Artworks = ({
  children,
  instantSearch,
  hideBanner = false,
}: Props) => {
  const { t } = useTranslation('artworks')
  const isMounted = useIsMounted()

  const [scrollbarVisible, setScrollbarVisible] = useState(false)
  const [showFilters, setShowFilters] = useState(true)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  const filtersContainerRef = useRef<HTMLDivElement | null>(null)
  const artworksContainerRef = useRef<HTMLDivElement | null>(null)

  const scrollToArtworks = useCallback(() => {
    if (artworksContainerRef?.current && filtersContainerRef?.current) {
      const artworkCtRect =
        artworksContainerRef.current?.getBoundingClientRect?.()
      const filtersCtRect =
        filtersContainerRef.current?.getBoundingClientRect?.()

      if (!artworkCtRect || !filtersCtRect) return

      const top = artworkCtRect.top + window.scrollY - filtersCtRect.height
      if (window.scrollY > top) {
        window.scrollTo({ top })
      }
    }
  }, [artworksContainerRef, filtersContainerRef])

  useDisableScroll({ disable: showMobileFilters })

  return (
    <>
      <div className='min-h-screen w-full'>
        {!hideBanner && (
          <>
            <div className='hidden sm:block'>
              <BannerHalfBlurred
                src='/files/banners/artworks-page-bg-big.jpg'
                priority
                reversed
                loading='eager'
                quality={80}
                sizes='(max-width:639px) 0vw, 100vw'
              >
                <BannerContent title={t('title')} description={t('desc')} />
              </BannerHalfBlurred>
            </div>

            <div className='bg-warm-50 py-5 px-6 sm:hidden space-y-1'>
              <h1 className='text-2xl font-medium font-body tracking-normal'>
                {t('title')}
              </h1>
              <p className='text-cool-700 text-sm'>
                <Trans
                  i18nKey='artworks:desc_mobile'
                  components={{
                    artists: (
                      <>
                        {artists.map((artist, index) => (
                          <Link
                            key={artist.name}
                            href={getArtistUrl(artist.name, artist.id)}
                          >
                            <span className='text-secondary-default underline'>
                              {artist.name}
                            </span>
                            {index !== artists.length - 1 && ', '}
                          </Link>
                        ))}
                      </>
                    ),
                  }}
                />
              </p>
            </div>
          </>
        )}

        {isMounted && (
          <InstantSearch index='filter' {...instantSearch}>
            {children}

            <Configure hitsPerPage={60} />

            <div
              style={
                showMobileFilters
                  ? { opacity: 1, pointerEvents: 'all' }
                  : { opacity: 0, pointerEvents: 'none' }
              }
              className='fixed left-0 top-0 z-50 h-screen w-full transition-opacity duration-[350ms] ease-in-out'
            >
              <Suspense>
                <Mobile setShowMobileFilters={setShowMobileFilters} />
              </Suspense>
            </div>

            <div
              ref={filtersContainerRef}
              className='sticky top-0 z-10 flex w-full items-center justify-end space-x-4 bg-cool-50 py-8'
            >
              <Suspense>
                <Header
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                  setShowMobileFilters={setShowMobileFilters}
                  scrollToArtworks={scrollToArtworks}
                />
              </Suspense>
            </div>

            <div
              className='container mb-16 md:mb-20'
              ref={artworksContainerRef}
            >
              <div className={cx({ 'flex items-start': showFilters })}>
                {showFilters && (
                  <div
                    onScrollCapture={() => setScrollbarVisible(true)}
                    onMouseLeave={() => setScrollbarVisible(false)}
                    className={classNames(
                      'sticky top-[5.2rem] -mt-6 hidden h-[90vh] overflow-y-scroll lg:mr-8 lg:block lg:w-[282px] lg:min-w-[282px]',
                      classes['custom-scroll'],
                      scrollbarVisible && classes['custom-scroll-visible']
                    )}
                  >
                    <Suspense>
                      <Filters
                        className='mt-6'
                        scrollToArtworks={scrollToArtworks}
                      />
                    </Suspense>
                  </div>
                )}

                <Suspense>
                  <List showFilters={showFilters} />
                </Suspense>
              </div>
            </div>
          </InstantSearch>
        )}
      </div>
    </>
  )
}
